import { type RankingAllData } from '@prisma/client'
import { prisma } from '#app/utils/db.server'

async function getPeerWindow(
	runner: RankingAllData,
	grouping: 'overall' | 'sex' | 'division' = 'overall',
): Promise<Array<RankingAllData & { formattedTime: string }>> {
	const window = await prisma.rankingAllData.findMany({
		where: {
			ageClassSexRank: {
				gte: runner.ageClassSexRank - 2,
				lte: runner.ageClassSexRank + 2,
			},
			sex: {
				equals: runner.sex,
			},
			ageClass: {
				equals: runner.ageClass,
			},
		},
		take: 5,
	})

	return window.map((entry: any) => ({ ...entry, formattedTime: formatTime(entry.time) }))
}

async function getRunnersByName(search?: string) {
	if (!search) {
		return []
	}

	const results = await prisma.rankingAllData.findMany({
		where: {
			fullName: {
				contains: search.toLowerCase(),
			},
		},
		take: 5,
	})
	return results
}

async function getRunner(name: string) {
	const runner = await prisma.rankingAllData.findFirst({
		where: {
			fullName: { equals: name },
		},
	})
	return runner
}

async function getRunnerById(id: string) {
	const runner = await prisma.rankingAllData.findFirstOrThrow({
		where: {
			athlete: { equals: id },
		},
		include: {
			athleteLink: {
				include: {
					Ranking: true,
				},
			},
		},
	})
	// const weeklyRank = extractWeeklyRank(runner.athleteLink.Ranking?.weeklyRank)
	return runner
}

export function extractWeeklyRank(encodedRanks: string) {
	return encodedRanks.split(',').map(pair => {
		const [dayOfYear, _rank] = pair.split(':')
		const rank = _rank === 'null' ? undefined : Number(_rank)
		return { x: dayOfYear, y: rank }
	})
}

async function getClosestPeer(data: Pick<RankingAllData, 'ageClass' | 'sex' | 'time'>) {
	const result = await prisma.$queryRaw`
		select * from RankingAllData
		where sex = ${data.sex} and
		      ageClass = ${data.ageClass}
		order by abs(time - ${data.time})
		limit 1
	`
	return (result as Array<RankingAllData>)[0]
}

async function getSample() {
	return await prisma.rankingAllData.findMany({
		take: 5,
	})
}

function makeTwoChars(x: number) {
	if (x < 10) return `0${x}`
	return x
}

type FilteredProps = {
	age_group: string
	sex: string
	country?: string
	state: string
	city?: string
	page?: number
}
async function getFiltered(props: FilteredProps) {
	// Do not allow requests past the 5th page
	let page: number = 0
	if (!props.page) page = 0
	else if (props.page > 5) page = 1
	else page = props.page

	const pageSize = 50
	const skip = 50 * page
	let where: any = {
		sex: {
			equals: props.sex,
		},
		ageClass: {
			equals: props.age_group,
		},
		athleteLink: {
			country: {
				equals: props.country,
			},
			state: {
				equals: props.state,
			},
			city: {
				equals: props.city,
			},
		},
	}

	// If the conditions are undefined, remove them.
	if (!props.country) delete where.athleteLink.country
	if (props.state === 'all') delete where.athleteLink.state
	if (!props.city) delete where.athleteLink.city
	if (props.age_group === 'all') delete where.ageClass
	if (props.sex === 'all') delete where.sex

	const results = await prisma.rankingAllData.findMany({
		include: {
			athleteLink: {
				include: {
					Ranking: true,
				},
			},
		},
		where,
		skip: skip,
		take: pageSize,
	})

	return results.map(runner => ({
		rank: runner.ageClassSexRank,
		Runner: runner.fullName,
		ageClass: runner.ageClass,
		state: runner.athleteLink.state,
		country: runner.athleteLink.country,
		rankChange: getRankChange(runner.athleteLink.Ranking?.weeklyRank || undefined),
		Time: formatTime(runner.time),
		id: runner.athlete,
	}))
}

function getRankChange(encodedRank: string | undefined) {
	try {
		if (typeof encodedRank === 'undefined') {
			throw new Error('undefined')
		}
		const weeklyRanks = encodedRank.split(',')
		const lastTwoRanks = weeklyRanks.filter((_, i, arr) => i >= arr.length - 2)
		const [penultimate, ultimate] = lastTwoRanks.map(encoded => encoded.split(':')[1])
		return Number(ultimate) - Number(penultimate)
	} catch (e) {
		console.log(e)
		return 0
	}
}

function formatTime(seconds: number) {
	const hours = Math.floor(seconds / (60 * 60))
	const minutes = Math.floor((seconds % (60 * 60)) / 60)
	const _seconds = seconds % 60
	return `${makeTwoChars(hours)}:${makeTwoChars(minutes)}:${makeTwoChars(_seconds)}`
}

async function getHistogramData(ageClass: string | null, sex: string | null) {
	// Redefining 'all' to be equal to null
	ageClass = ageClass === 'all' ? null : ageClass

	let results: any
	if (ageClass && sex) {
		results = await prisma.$queryRaw`
		with base as (select * from RankingAllData where time < 25200)
		select bucket, sex as "group", cast(count(*) as int) as count
		from (
			select athlete, sex, round(time / 300, 0) as bucket
			from base
			where ageClass = ${ageClass} and
			      sex = ${sex}
		)
		group by bucket
		order by bucket desc
	`
	}
	if (!ageClass && sex) {
		results = await prisma.$queryRaw`
		with base as (select * from RankingAllData where time < 28800)
  	select bucket, sex as "group", cast(count(*) as int) as count
  	from (
  		select athlete, sex, round(time / 300, 0) as bucket
  		from base
  		where sex = ${sex}
  	)
  	group by bucket
  	order by bucket desc
  `
	}
	if (ageClass && !sex) {
		results = await prisma.$queryRaw`
		with base as (select * from RankingAllData where time < 36000)
  	select bucket, sex as "group", cast(count(*) as int) as count
  	from (
  		select athlete, sex, round(time / 300, 0) as bucket
  		from base
      where ageClass = ${ageClass}
  	)
  	group by bucket, sex
  	order by bucket desc
  `
	}
	if (!ageClass && !sex) {
		results = await prisma.$queryRaw`
		with base as (select * from RankingAllData where time < 36000)
  	select bucket, 'all' as "group", cast(count(*) as int) as count
  	from (
  		select athlete, round(time / 300, 0) as bucket
  		from base
  	)
  	group by bucket
  	order by bucket desc
  `
	}
	return results as Array<{ bucket: number; group: string; count: number }>
}

async function getResultsById(id: string) {
	const results = await prisma.results.findMany({
		where: {
			athlete: { equals: id },
		},
		orderBy: {
			date: 'desc',
		},
	})
	return results
}

async function getFastestTime(
	sex: 'm' | 'f' = 'm',
	criteria: 'overallRank' | 'ageGradedPercent' = 'overallRank',
) {
	const results = await prisma.rankingAllData.findMany({
		where: {
			sex: sex,
		},
		orderBy: {
			[criteria]: 'desc',
		},
		select: {
			fullName: true,
			ageClass: true,
			ageGradedPercent: true,
			time: true,
			date: true,
			athlete: true,
		},
		take: 3,
	})

	return results.map(result => ({
		fullName: `${result.fullName}` ?? 'Your Name Here',
		id: result.athlete,
		ageClass: result.ageClass,
		ageGradedPercent: result.ageGradedPercent,
		time: result.time ? formatTime(result.time) : 'No Times Yet This Month',
		date: result.date.toString(),
	}))
}

async function getMostRacesRun(limit = 10) {
	const [result] = (await prisma.$queryRaw`
		  select count, group_concat(fullName||'-'||id, ',') as names from
			(select fullName, id from Athlete) a inner join
		  (select athlete, count(*) as count from Results
			 where athlete not in (select id from athlete where fullName in ('unknown runner', 'anonymous participant'))
			 group by athlete
			 having count(*) > 1
			 order by count(*) desc
			 limit ${limit}) b
			on a.id = b.athlete
			group by count
			order by count desc
			limit 1`) as any

	return { names: result.names as string, count: result.count as number }
}

export {
	getPeerWindow,
	getClosestPeer,
	getRunnersByName,
	getSample,
	getRunner,
	getResultsById,
	getRunnerById,
	getFiltered,
	getHistogramData,
	formatTime,
	getFastestTime,
	getMostRacesRun,
}
